<div class="om-page">
  <app-project-nav></app-project-nav>
  <app-page-header
    [eyebrow]="'objectMappings.recording.liveLabel' | translate"
    [title]="libraryName() ?? ('objectMappings.recording.titleFallback' | translate)"
  />

  @if (connectionLost()) {
    <p class="rec-banner" role="status" data-testid="om-rec-connection-lost">
      <mat-icon aria-hidden="true">sync_problem</mat-icon>
      {{ 'objectMappings.recording.connectionLost' | translate }}
    </p>
  }

  @if (state() === 'loading') {
    <div class="om-loading" data-testid="om-rec-loading">
      <mat-spinner diameter="32" />
    </div>
  } @else if (state() === 'notFound') {
    <p class="om-hint om-hint--error" role="alert" data-testid="om-rec-not-found">
      {{ 'objectMappings.recording.notFound' | translate }}
    </p>
  } @else if (state() === 'error') {
    <div class="om-hint om-hint--error" role="alert" data-testid="om-rec-error">
      <p>{{ 'objectMappings.recording.loadError' | translate }}</p>
      <button mat-stroked-button type="button" data-testid="om-rec-retry" (click)="retry()">
        {{ 'objectMappings.retry' | translate }}
      </button>
    </div>
  } @else if (job(); as current) {
    <section class="rec-live" [class.rec-live--active]="phase() === 'RUNNING'">
      <div
        class="rec-live__body"
        [attr.role]="active() ? 'status' : null"
        [attr.aria-live]="active() ? 'polite' : null"
      >
        <div class="rec-live__meta">
          <span data-testid="om-rec-status">
            @if (phase() === 'RUNNING') {
              <span class="rec-running">
                <span class="rec-running__dot" aria-hidden="true"></span>
                {{ 'objectMappings.recording.status.RUNNING' | translate }}
              </span>
            } @else {
              <app-status-pill
                size="sm"
                [kind]="statusPill()"
                [label]="'objectMappings.recording.status.' + phase() | translate"
              />
            }
          </span>
          <span class="rec-live__fact" data-testid="om-rec-runner">
            {{ 'objectMappings.recording.runner' | translate }}:
            <strong>{{ current.runnerName ?? ('objectMappings.recording.runnerUnknown' | translate) }}</strong>
          </span>
          <span class="rec-live__fact" data-testid="om-rec-runtime">
            {{ 'objectMappings.recording.runtime' | translate }}:
            <strong>{{ runtime() ?? '–' }}</strong>
          </span>
        </div>
        @if (phase() === 'QUEUED') {
          <p class="rec-live__text" data-testid="om-rec-waiting">
            {{ 'objectMappings.recording.waiting' | translate }}
          </p>
        } @else if (phase() === 'RUNNING') {
          <p class="rec-live__text">{{ 'objectMappings.recording.savedHint' | translate }}</p>
        } @else if (phase() === 'ENDED') {
          <p class="rec-live__text" data-testid="om-rec-ended">{{ 'objectMappings.recording.ended' | translate }}</p>
        }
        @if (cancelRequested()) {
          <p class="rec-live__text" data-testid="om-rec-stopping">
            {{ 'objectMappings.recording.stopping' | translate }}
          </p>
        }
        @if (stopNotice(); as notice) {
          <p class="rec-live__text rec-live__text--error" role="alert" data-testid="om-rec-stop-notice">
            {{
              (notice === 'alreadyFinished'
                ? 'objectMappings.recording.stopAlreadyFinished'
                : 'objectMappings.recording.stopFailed'
              ) | translate
            }}
          </p>
        }
      </div>
      <div class="rec-live__actions">
        @if (canStop()) {
          <button
            mat-stroked-button
            type="button"
            data-testid="om-rec-stop"
            [disabled]="stopping() || cancelRequested()"
            (click)="stop()"
          >
            <mat-icon aria-hidden="true">stop</mat-icon>
            {{ 'objectMappings.recording.stop' | translate }}
          </button>
        }
        @if (finished()) {
          <a mat-flat-button data-testid="om-rec-review" [routerLink]="reviewLink()">
            {{ 'objectMappings.recording.review' | translate }}
          </a>
        }
      </div>
    </section>

    @if (phase() === 'ERROR') {
      <div class="rec-failed" role="alert">
        <mat-icon aria-hidden="true">error</mat-icon>
        <p data-testid="om-rec-failed">
          {{ 'objectMappings.recording.errorEnded' | translate: { error: current.error ?? '' } }}
        </p>
      </div>
    }

    <div class="rec-layout">
      <div class="rec-main">
        @if (rows().length === 0) {
          @if (phase() === 'RUNNING') {
            <p class="om-hint" data-testid="om-rec-empty">{{ 'objectMappings.recording.empty' | translate }}</p>
          }
        } @else {
          <ul class="om-card rec-list" [attr.aria-label]="'objectMappings.recording.listLabel' | translate">
            @for (row of rows(); track row.id) {
              <li class="rec-capture" data-testid="om-rec-capture">
                @if (row.showImage && imageUrl(row.id); as src) {
                  <img class="rec-capture__image" data-testid="om-rec-capture-image" alt="" [src]="src" />
                } @else {
                  <span
                    class="rec-capture__image rec-capture__image--empty"
                    data-testid="om-rec-capture-placeholder"
                    [attr.aria-label]="'objectMappings.recording.noImage' | translate"
                  >
                    <mat-icon aria-hidden="true">{{ row.nameKey === 'passwordField' ? 'password' : 'image' }}</mat-icon>
                  </span>
                }
                <span class="rec-capture__name" data-testid="om-rec-capture-name">
                  {{ row.name ?? ('objectMappings.recording.' + row.nameKey | translate) }}
                </span>
                <span class="rec-capture__best" data-testid="om-rec-capture-best">
                  @if (row.best; as best) {
                    <span class="om-strategy">{{ best.strategy }}</span>
                    <code class="om-locator">{{ best.value }}</code>
                  }
                </span>
                @if (row.pill === 'stable') {
                  <app-status-pill
                    class="rec-pill"
                    kind="aktiv"
                    size="sm"
                    [label]="'objectMappings.recording.pill.stable' | translate"
                    data-testid="om-rec-pill-stable"
                  />
                } @else if (row.pill === 'positionOnly') {
                  <app-status-pill
                    class="rec-pill"
                    kind="pending"
                    size="sm"
                    [label]="'objectMappings.recording.pill.positionOnly' | translate"
                    data-testid="om-rec-pill-position"
                  />
                }
              </li>
            }
          </ul>
        }
      </div>
      <aside class="om-card rec-counts">
        <h2 class="rec-counts__title">{{ 'objectMappings.recording.counts.title' | translate }}</h2>
        <dl>
          <div data-testid="om-rec-count-captured">
            <dt>{{ 'objectMappings.recording.counts.captured' | translate }}</dt>
            <dd>{{ counts().captured }}</dd>
          </div>
          <div data-testid="om-rec-count-stable">
            <dt>{{ 'objectMappings.recording.counts.stable' | translate }}</dt>
            <dd>{{ counts().stable }}</dd>
          </div>
          <div data-testid="om-rec-count-position">
            <dt>{{ 'objectMappings.recording.counts.positionOnly' | translate }}</dt>
            <dd>{{ counts().positionOnly }}</dd>
          </div>
          <div data-testid="om-rec-count-name-taken">
            <dt>{{ 'objectMappings.recording.counts.nameTaken' | translate }}</dt>
            <dd>{{ counts().nameTaken }}</dd>
          </div>
        </dl>
      </aside>
    </div>
  }
</div>
